import React, { useEffect, useState, useMemo } from "react";
import {
Chart as ChartJS,
CategoryScale,
LinearScale,
PointElement,
LineElement,
Title,
Tooltip,
Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import {
Listbox,
ListboxButton,
ListboxOption,
ListboxOptions,
Combobox,
} from "@headlessui/react";
import { collection, getDocs } from "firebase/firestore";
import { ReactComponent as ComprasIcon } from '../../assets/icons/ComprasIcon.svg';
import { ReactComponent as EstadisticasIcon } from '../../assets/icons/EstadisticasIcon.svg';
import { ReactComponent as TrofeoIcon } from '../../assets/icons/TrofeoIcon.svg';
import { ReactComponent as MiembrosIcon } from '../../assets/icons/MiembrosIcon.svg';
import { ReactComponent as EventosIcon } from '../../assets/icons/EventosIcon.svg';
import { ReactComponent as ClientesIcon } from '../../assets/icons/ClientesIcon.svg';
import { db } from "../../services/firebase";
import { useCotizaciones, aPesos as aPesosCompartido } from "../../services/cotizaciones";
import LoadingSpinner from "../LoadingSpinner";
ChartJS.register(
CategoryScale,
LinearScale,
PointElement,
LineElement,
Title,
Tooltip,
Legend
);
const EstadisticasCompras = () => {
    const [query, setQuery] = useState('');
    const [compras, setCompras] = useState([]);
    const [tipoGraficoCompras, setTipoGraficoCompras] = useState('mensual');
    const [tipoGraficoArticulo, setTipoGraficoArticulo] = useState('mensual');
    const [dataChart, setDataChart] = useState({ labels: [], datasets: [] });
    const [articulos, setArticulos] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [articuloSeleccionado, setArticuloSeleccionado] = useState('');
    const [dataChartArticulo, setDataChartArticulo] = useState({
        labels: [],
        datasets: [],
    });
    const [monedaSeleccionada, setMonedaSeleccionada] = useState('ARS');
    // Cotización del día para Dólares/Euros/Reales, compartida con EstadisticasVentas
    // (antes cada pantalla hacía su propio fetch y solo sabía convertir Dólares).
    const { cotizaciones } = useCotizaciones();
    const dolarOficialVenta = cotizaciones?.Dólares ?? null;
    const dolarCCLVenta = cotizaciones?.DólaresCCL ?? null;
const formatPrice = (value) => {
    // 1. Convertir el valor a número flotante
    const numericValue = parseFloat(value);
    
    // 2. Redondear siempre hacia abajo (Math.floor)
    const flooredValue = Math.floor(numericValue); // Esto convierte 25007.50 a 25007
    
    // 3. Aplicar el formato
    const locale = 'es-AR';
    // Se mantienen en 0 las fracciones decimales para que el resultado final sea un entero
    const options = { minimumFractionDigits: 0, maximumFractionDigits: 0 }; 
    
    return flooredValue.toLocaleString(locale, options);
};
    // Normaliza un monto a Pesos según la moneda en la que esté esa compra/artículo
    // (antes se sumaba todo como si fuera ARS, así que una compra en dólares
    // "pesaba" lo mismo en pesos que en dólares y rompía los totales). Ahora
    // usa el servicio compartido de cotizaciones, que también cubre Reales/Euros.
    const aPesos = (monto, tipoDivisa) => aPesosCompartido(monto, tipoDivisa, cotizaciones);
    const convertAmount = (amount) => {
        const numericAmount = parseFloat(amount) || 0;
        if (monedaSeleccionada === 'DOLAR' && dolarOficialVenta) {
            return numericAmount / dolarOficialVenta;
        }
        if (monedaSeleccionada === 'CCL' && dolarCCLVenta) {
            return numericAmount / dolarCCLVenta;
        }
        return numericAmount;
    };
    const formatConvertedPrice = (value) => {
        const convertedValue = convertAmount(value);
        const prefix = monedaSeleccionada === 'ARS' ? '$' : 'US$';
        const formattedPrice = formatPrice(convertedValue);
        return `${prefix}${formattedPrice}`;
    };
    const formatConvertedNumberForChart = (value) => {
        const convertedValue = convertAmount(value);
        const prefix = monedaSeleccionada === 'ARS' ? '$' : 'US$';
        let formattedValue;
        if (convertedValue >= 1000000) {
            formattedValue = (convertedValue / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
        } else if (convertedValue >= 1000) {
            formattedValue = (convertedValue / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
        } else {
            formattedValue = convertedValue.toLocaleString('es-AR', { maximumFractionDigits: 0 });
        }
        return `${prefix}${formattedValue}`;
    };
    useEffect(() => {
        const fetchData = async () => {
            try {
                const comprasSnapshot = await getDocs(collection(db, 'compras'));
                const comprasData = comprasSnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                setCompras(comprasData);
                const articulosUnicos = new Set();
                comprasData.forEach((compra) => {
                    compra.articulos?.forEach((a) =>
                        articulosUnicos.add(a.nombre)
                    );
                });
                const articulosArray = Array.from(articulosUnicos);
                setArticulos(articulosArray);
                if (articulosArray.length > 0)
                    setArticuloSeleccionado(articulosArray[0]);
                setIsLoading(false);
            } catch (error) {
                console.error('Error fetching data:', error);
                setIsLoading(false);
            }
        };
        fetchData();
    }, [db]);
    useEffect(() => {
        const hoy = new Date();
        let labels = [];
        let data = [];
        const mesesMap = { bimestral: 2, trimestral: 3, semestral: 6, anual: 12 };
        const tipoGrafico = tipoGraficoCompras;
        const totalComprasProp = 'totalConImpuestos';
        const agregarDatos = (fechaVenta, totalFinal, tipoDivisa) => {
            const fechaCompra = fechaVenta?.toDate
                ? fechaVenta.toDate()
                : new Date(fechaVenta);
            if (tipoGrafico === 'mensual') {
                for (let i = 0; i < 30; i++) {
                    const fecha = new Date(hoy);
                    fecha.setDate(hoy.getDate() - (29 - i));
                    if (
                        fechaCompra.getFullYear() === fecha.getFullYear() &&
                        fechaCompra.getMonth() === fecha.getMonth() &&
                        fechaCompra.getDate() === fecha.getDate()
                    ) {
                        data[i] += aPesos(totalFinal, tipoDivisa);
                        break;
                    }
                }
            } else if (tipoGrafico === 'semanal') {
                for (let i = 0; i < 7; i++) {
                    const fecha = new Date(hoy);
                    fecha.setDate(hoy.getDate() - (6 - i));
                    if (
                        fechaCompra.getFullYear() === fecha.getFullYear() &&
                        fechaCompra.getMonth() === fecha.getMonth() &&
                        fechaCompra.getDate() === fecha.getDate()
                    ) {
                        data[i] += aPesos(totalFinal, tipoDivisa);
                        break;
                    }
                }
            } else {
                const cantidadMeses = mesesMap[tipoGrafico] || 12;
                for (let i = 0; i < labels.length; i++) {
                    const fecha = new Date(
                        hoy.getFullYear(),
                        hoy.getMonth() - (labels.length - 1 - i),
                        1
                    );
                    if (
                        fechaCompra.getFullYear() === fecha.getFullYear() &&
                        fechaCompra.getMonth() === fecha.getMonth()
                    ) {
                        data[i] += aPesos(totalFinal, tipoDivisa);
                        break;
                    }
                }
            }
        };
        if (tipoGrafico === 'mensual') {
            for (let i = 29; i >= 0; i--) {
                const fecha = new Date(hoy);
                fecha.setDate(hoy.getDate() - i);
                labels.push(`${fecha.getDate()}/${fecha.getMonth() + 1}`);
                data.push(0);
            }
        } else if (tipoGrafico === 'semanal') {
            for (let i = 6; i >= 0; i--) {
                const fecha = new Date(hoy);
                fecha.setDate(hoy.getDate() - i);
                labels.push(`${fecha.getDate()}/${fecha.getMonth() + 1}`);
                data.push(0);
            }
        } else {
            const cantidadMeses = mesesMap[tipoGrafico] || 12;
            for (let i = cantidadMeses - 1; i >= 0; i--) {
                const fecha = new Date(hoy.getFullYear(), hoy.getMonth() - i, 1);
                labels.push(
                    fecha.toLocaleString('default', { month: 'short', year: 'numeric' })
                );
                data.push(0);
            }
        }
        compras.forEach((compra) => {
            agregarDatos(compra.fechaCompra, compra[totalComprasProp], compra.tipoDivisa);
        });
        const convertedData = data.map(convertAmount);
        setDataChart({
            labels,
            datasets: [
                {
                    label: `Compras (${tipoGrafico})`,
                    data: convertedData,
                    borderColor: "#e53935",
                    backgroundColor: "rgba(255,0,0,0.2)",
                    borderWidth: 2,
                    tension: 0.01,
                    pointBackgroundColor: "#e53935",
                    pointBorderColor: "#e53935",
                    pointRadius: 1,
                    pointHoverRadius: 2,
                    pointStyle: "circle",
                },
            ],
        });
    }, [compras, tipoGraficoCompras, monedaSeleccionada, dolarOficialVenta, dolarCCLVenta]);
    useEffect(() => {
        if (!articuloSeleccionado) return;
        const hoy = new Date();
        let labels = [];
        let data = [];
        const mesesMap = { bimestral: 2, trimestral: 3, semestral: 6, anual: 12 };
        const tipoGrafico = tipoGraficoArticulo;
        const agregarDatosArticulo = (fechaVenta, cantidad, precioUnidad, tipoDivisa) => {
            const montoArticulo =
                aPesos((parseInt(cantidad) || 0) * (parseFloat(precioUnidad) || 0), tipoDivisa);
            const fechaCompra = fechaVenta?.toDate
                ? fechaVenta.toDate()
                : new Date(fechaVenta);
            if (tipoGrafico === 'mensual') {
                for (let i = 0; i < 30; i++) {
                    const fecha = new Date(hoy);
                    fecha.setDate(hoy.getDate() - (29 - i));
                    if (
                        fechaCompra.getFullYear() === fecha.getFullYear() &&
                        fechaCompra.getMonth() === fecha.getMonth() &&
                        fechaCompra.getDate() === fecha.getDate()
                    ) {
                        data[i] += montoArticulo;
                        break;
                    }
                }
            } else if (tipoGrafico === 'semanal') {
                for (let i = 0; i < 7; i++) {
                    const fecha = new Date(hoy);
                    fecha.setDate(hoy.getDate() - (6 - i));
                    if (
                        fechaCompra.getFullYear() === fecha.getFullYear() &&
                        fechaCompra.getMonth() === fecha.getMonth() &&
                        fechaCompra.getDate() === fecha.getDate()
                    ) {
                        data[i] += montoArticulo;
                        break;
                    }
                }
            } else {
                const cantidadMeses = mesesMap[tipoGrafico] || 12;
                for (let i = 0; i < labels.length; i++) {
                    const fecha = new Date(
                        hoy.getFullYear(),
                        hoy.getMonth() - (labels.length - 1 - i),
                        1
                    );
                    if (
                        fechaCompra.getFullYear() === fecha.getFullYear() &&
                        fechaCompra.getMonth() === fecha.getMonth()
                    ) {
                        data[i] += montoArticulo;
                        break;
                    }
                }
            }
        };
        if (tipoGrafico === 'mensual') {
            for (let i = 29; i >= 0; i--) {
                const fecha = new Date(hoy);
                fecha.setDate(hoy.getDate() - i);
                labels.push(`${fecha.getDate()}/${fecha.getMonth() + 1}`);
                data.push(0);
            }
        } else if (tipoGrafico === 'semanal') {
            for (let i = 6; i >= 0; i--) {
                const fecha = new Date(hoy);
                fecha.setDate(hoy.getDate() - i);
                labels.push(`${fecha.getDate()}/${fecha.getMonth() + 1}`);
                data.push(0);
            }
        } else {
            const cantidadMeses = mesesMap[tipoGrafico] || 12;
            for (let i = cantidadMeses - 1; i >= 0; i--) {
                const fecha = new Date(hoy.getFullYear(), hoy.getMonth() - i, 1);
                labels.push(
                    fecha.toLocaleString('default', { month: 'short', year: 'numeric' })
                );
                data.push(0);
            }
        }
        compras.forEach((compra) => {
            compra.articulos?.forEach((a) => {
                if (a.nombre === articuloSeleccionado) {
                    agregarDatosArticulo(
                        compra.fechaCompra,
                        a.cantidadUnidades,
                        a.precioUnidad,
                        a.tipoDivisa || compra.tipoDivisa
                    );
                }
            });
        });
        const convertedData = data.map(convertAmount);
        setDataChartArticulo({
            labels,
            datasets: [
                {
                    label: `Compras de ${articuloSeleccionado} (${tipoGrafico})`,
                    data: convertedData,
                    borderColor: "#e53935",
                    backgroundColor: "rgba(255,0,0,0.2)",
                    borderWidth: 2,
                    tension: 0.01,
                    pointBackgroundColor: "#e53935",
                    pointBorderColor: "#e53935",
                    pointRadius: 1,
                    pointHoverRadius: 2,
                    pointStyle: "circle",
                },
            ],
        });
    }, [
        compras,
        articuloSeleccionado,
        tipoGraficoArticulo,
        monedaSeleccionada,
        dolarOficialVenta,
        dolarCCLVenta,
    ]);
    const totalComprasPeriodo =
        dataChart.datasets[0]?.data.reduce((acc, val) => acc + val, 0) || 0;
    const totalArticuloPeriodo =
        dataChartArticulo.datasets[0]?.data.reduce((acc, val) => acc + val, 0) || 0;
    const getMultiplier = () =>
        monedaSeleccionada === 'ARS'
            ? 1
            : monedaSeleccionada === 'DOLAR'
            ? dolarOficialVenta
            : dolarCCLVenta || 1;
    const options = {
        responsive: true,
        plugins: {
            tooltip: {
                enabled: true,
                backgroundColor: "#fff",
                titleColor: "#000",
                bodyColor: "#000",
                borderColor: "#e53935",
                borderWidth: 1,
                cornerRadius: 12,
                displayColors: false,
                caretSize: 0,
                padding: {
                    top: 3,
                    bottom: 3,
                    left: 10,
                    right: 10,
                },
                callbacks: {
                    title: () => null,
                    label: (context) => formatConvertedPrice(context.raw * (monedaSeleccionada === 'ARS' ? 1 : (monedaSeleccionada === 'DOLAR' ? dolarOficialVenta : dolarCCLVenta) || 1)), 
                },
            },
            legend: { display: false },
            title: {
                display: true,
                text: `        Compras totales del período: ${formatConvertedPrice(
                    totalComprasPeriodo * getMultiplier()
                )}`,
                color: "black",
                font: {
                    size: 14,
                    weight: "normal",
                    family: "Effra",
                },
                align: "start",
                padding: { top: 20, bottom: 30 },
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: { display: false },
                ticks: {
                    color: "black",
                    font: {
                        size: 11,
                        weight: "normal",
                        family: "Effra",
                    },
                    callback: function (value) {
                        return formatConvertedNumberForChart(value * (monedaSeleccionada === 'ARS' ? 1 : (monedaSeleccionada === 'DOLAR' ? dolarOficialVenta : dolarCCLVenta) || 1));
                    },
                },
            },
            x: {
                grid: { display: false },
                ticks: {
                    color: "black",
                    font: {
                        size: 11,
                        weight: "normal",
                        family: "Effra",
                    },
                },
            },
        },
    };
    const optionsArticulo = {
        ...options,
        responsive: true,
        plugins: {
            tooltip: {
                enabled: true,
                backgroundColor: "#fff",
                titleColor: "#000",
                bodyColor: "#000",
                borderColor: "#e53935",
                borderWidth: 1,
                cornerRadius: 12,
                displayColors: false,
                caretSize: 0,
                padding: {
                    top: 3,
                    bottom: 3,
                    left: 10,
                    right: 10,
                },
                callbacks: {
                    title: () => null,
                    label: (context) => formatConvertedPrice(context.raw * (monedaSeleccionada === 'ARS' ? 1 : (monedaSeleccionada === 'DOLAR' ? dolarOficialVenta : dolarCCLVenta) || 1)),
                },
            },
            legend: { display: false },
            title: {
                display: true,
                text: `        Compras totales del período: ${formatConvertedPrice(
                    totalArticuloPeriodo * getMultiplier()
                )}`,
                color: "black",
                font: {
                    size: 14,
                    weight: "normal",
                    family: "Effra",
                },
                align: "start",
                padding: { bottom: 30, top: 20 },
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: { display: false },
                ticks: {
                    color: "black",
                    font: {
                        size: 11,
                        weight: "normal",
                        family: "Effra",
                    },
                    callback: function (value) {
                        return formatConvertedNumberForChart(value * (monedaSeleccionada === 'ARS' ? 1 : (monedaSeleccionada === 'DOLAR' ? dolarOficialVenta : dolarCCLVenta) || 1));
                    },
                },
            },
            x: {
                grid: { display: false },
                ticks: {
                    color: "black",
                    font: {
                        size: 11,
                        weight: "normal",
                        family: "Effra",
                    },
                },
            },
        },
    };
    const topArticulos = useMemo(() => {
        const totales = {};
        compras.forEach((compra) => {
            compra.articulos?.forEach((a) => {
                const monto = aPesos((parseFloat(a.cantidadUnidades) || 0) * (parseFloat(a.precioUnidad) || 0), a.tipoDivisa || compra.tipoDivisa);
                if (totales[a.nombre]) {
                    totales[a.nombre] += monto;
                } else {
                    totales[a.nombre] = monto;
                }
            });
        });
        const sorted = Object.entries(totales)
            .map(([nombre, total]) => ({ nombre, total }))
            .sort((a, b) => b.total - a.total);
        return sorted.slice(0, 5);
    }, [compras, dolarOficialVenta]);
    const topProveedores = useMemo(() => {
        const totales = {};
        compras.forEach((compra) => {
            const proveedorId =
                compra.cuitProveedor ||
                compra.razonSocial ||
                compra.numeroFactura ||
                'Proveedor Desconocido';
            const nombre = compra.razonSocial || 'Proveedor Desconocido';
            const totalCompra = aPesos(compra.totalConImpuestos, compra.tipoDivisa);
            if (totales[proveedorId]) {
                totales[proveedorId].total += totalCompra;
            } else {
                totales[proveedorId] = { nombre, total: totalCompra };
            }
        });
        const sorted = Object.values(totales).sort((a, b) => b.total - a.total);
        return sorted.slice(0, 5);
    }, [compras, dolarOficialVenta]);
    const periodos = [
        { value: 'semanal', label: 'Semanales' },
        { value: 'mensual', label: 'Mensuales' },
        { value: 'bimestral', label: 'Bimestrales' },
        { value: 'trimestral', label: 'Trimestrales' },
        { value: 'semestral', label: 'Semestrales' },
        { value: 'anual', label: 'Anuales' },
    ];
    const filteredArticulos =
        query === ''
            ? articulos
            : articulos.filter((articulo) => {
                  return articulo.toLowerCase().includes(query.toLowerCase());
              });
    if (isLoading) return <LoadingSpinner />;
    return (
        <div
            style={{ padding: '0 16px', position: 'relative', overflow: 'auto' }}
            className="Trazabilidad"
        >
            <header
                className="trazabilidad-header"
                style={{
                    position: 'sticky',
                    top: '0',
                    backgroundColor: 'white',
                    zIndex: 10,
                }}
            >
                <div
                    className="header-content"
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px 0',
                    }}
                >
                    <h1>Estadísticas de Compras</h1>
                </div>
            </header>
            <div className="container" style={{ margin: '0 10px' }}>
                <div className="formtitle-container">
                    <div className="formicon-container">
                        <ComprasIcon
                            className="formicon"
                            style={{ height: '20px', margin: 'auto' }}
                        />
                    </div>
                    <h1 className="formtitle">Compras</h1>
                </div>
                <hr className="divider" style={{ marginBottom: '0' }} />
                {}
                <div className="currency-selector-container">
                    <span>Valores expresados en:</span>
                    <button
                        className={`currency-button ${
                            monedaSeleccionada === 'ARS' ? 'active' : ''
                        }`}
                        onClick={() => setMonedaSeleccionada('ARS')}
                    >
                        ARS
                    </button>
                    <button
                        className={`currency-button ${
                            monedaSeleccionada === 'DOLAR' ? 'active' : ''
                        }`}
                        onClick={() => setMonedaSeleccionada('DOLAR')}
                        disabled={!dolarOficialVenta}
                    >
                        BNA
                    </button>
                    <button
                        className={`currency-button ${
                            monedaSeleccionada === 'CCL' ? 'active' : ''
                        }`}
                        onClick={() => setMonedaSeleccionada('CCL')}
                        disabled={!dolarCCLVenta}
                    >
                        CCL
                    </button>
                </div>
                <div className="estadisticas-container">
                    {}
                    <div className="estadisticas-charts-row">
                        {}
                        <div className="chart-container chart-general">
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    borderBottom: '1px solid #e0e3e7',
                                    padding: '0px 0 5px',
                                }}
                            >
                                <h3 className="chart-subtitle">
                                    <EstadisticasIcon
                                        className="menu-icon"
                                        style={{ width: '12px' }}
                                    />
                                    Gráfico de Compras
                                </h3>
                                <div className="input-group">
                                    <Listbox
                                        value={tipoGraficoCompras}
                                        onChange={setTipoGraficoCompras}
                                    >
                                        {({ open }) => (
                                            <>
                                                <ListboxButton
                                                    className={`custom-listbox-button ${
                                                        open ? 'open' : ''
                                                    }`}
                                                    style={{
                                                        minWidth: '140px',
                                                        padding: '1px 7px 1px 15px',
                                                    }}
                                                >
                                                    {
                                                        periodos.find(
                                                            (p) =>
                                                                p.value === tipoGraficoCompras
                                                        )?.label
                                                    }
                                                    <span className="custom-arrow-container">
                                                        <span className="custom-arrow"></span>
                                                    </span>
                                                </ListboxButton>
                                                <ListboxOptions className="custom-listbox-options">
                                                    {periodos.map((periodo) => (
                                                        <ListboxOption
                                                            key={periodo.value}
                                                            value={periodo.value}
                                                            className="custom-listbox-option"
                                                        >
                                                            {periodo.label}
                                                        </ListboxOption>
                                                    ))}
                                                </ListboxOptions>
                                            </>
                                        )}
                                    </Listbox>
                                </div>
                            </div>
                            <Line data={dataChart} options={options} className="line-chart" />
                        </div>
                        {}
                        <div className="chart-container chart-producto">
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    borderBottom: '1px solid #e0e3e7',
                                    padding: '0px 0 5px',
                                }}
                            >
                                <h3 className="chart-subtitle">
                                    <EstadisticasIcon
                                        className="menu-icon"
                                        style={{ width: '12px' }}
                                    />
                                    Compras por Artículo
                                </h3>
                                <div className="input-group">
                                    <Combobox
                                        value={articuloSeleccionado}
                                        onChange={(value) => {
                                            setArticuloSeleccionado(value);
                                            setQuery('');
                                        }}
                                    >
                                        {({ open }) => (
                                            <>
                                                <div style={{ position: 'relative' }}>
                                                    <Combobox.Button
                                                        as="div"
                                                        className={`custom-listbox-button ${
                                                            open ? 'open' : ''
                                                        }`}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            maxWidth: '220px',
                                                            padding: '1px 7px 1px 10px',
                                                            cursor: 'pointer',
                                                        }}
                                                    >
                                                        <Combobox.Input
                                                            className="custom-combobox-input"
                                                            placeholder={
                                                                articuloSeleccionado ||
                                                                'Selecciona un artículo'
                                                            }
                                                            value={query}
                                                            onChange={(event) =>
                                                                setQuery(event.target.value)
                                                            }
                                                            style={{
                                                                flexGrow: 1,
                                                                marginRight: '8px',
                                                                padding: '0',
                                                                border: 'none',
                                                                outline: 'none',
                                                                backgroundColor:
                                                                    'transparent',
                                                            }}
                                                        />
                                                        <span
                                                            className="custom-arrow-container"
                                                            style={{ flexShrink: 0 }}
                                                        >
                                                            <span className="custom-arrow"></span>
                                                        </span>
                                                    </Combobox.Button>
                                                    <Combobox.Options
                                                        className="custom-listbox-options"
                                                        style={{ width: '227.5px' }}
                                                    >
                                                        {filteredArticulos.map((p) => (
                                                            <Combobox.Option
                                                                key={p}
                                                                value={p}
                                                                className="custom-listbox-option"
                                                            >
                                                                {p}
                                                            </Combobox.Option>
                                                        ))}
                                                    </Combobox.Options>
                                                </div>
                                            </>
                                        )}
                                    </Combobox>
                                </div>
                                <div className="input-group">
                                    <Listbox
                                        value={tipoGraficoArticulo}
                                        onChange={setTipoGraficoArticulo}
                                    >
                                        {({ open }) => (
                                            <>
                                                <ListboxButton
                                                    className={`custom-listbox-button ${
                                                        open ? 'open' : ''
                                                    }`}
                                                    style={{
                                                        minWidth: '140px',
                                                        padding: '1px 7px 1px 15px',
                                                    }}
                                                >
                                                    {
                                                        periodos.find(
                                                            (p) =>
                                                                p.value ===
                                                                tipoGraficoArticulo
                                                        )?.label
                                                    }
                                                    <span className="custom-arrow-container">
                                                        <span className="custom-arrow"></span>
                                                    </span>
                                                </ListboxButton>
                                                <ListboxOptions className="custom-listbox-options">
                                                    {periodos.map((periodo) => (
                                                        <ListboxOption
                                                            key={periodo.value}
                                                            value={periodo.value}
                                                            className="custom-listbox-option"
                                                        >
                                                            {periodo.label}
                                                        </ListboxOption>
                                                    ))}
                                                </ListboxOptions>
                                            </>
                                        )}
                                    </Listbox>
                                </div>
                            </div>
                            <Line
                                data={dataChartArticulo}
                                options={optionsArticulo}
                                className="line-chart"
                            />
                        </div>
                    </div>
                    {}
                    <div className="top-lists-row" style={{ display: 'flex', gap: '15px', paddingBottom:'25px' }}>
                        {}
                        <div className="top-list top-productos" style={{ flex: 1 }}>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: '10px',
                                    alignItems: 'center',
                                    paddingBottom: '10px',
                                }}
                            >
                                <TrofeoIcon
                                    className="formicon"
                                    style={{
                                        width: '17px',
                                        margin: '0',
                                        fill: '#2c2c2c',
                                    }}
                                />
                                <h3 className="top-list-title">Top 5 Productos</h3>
                            </div>
                            <ol className="top-list-items">
                                {topArticulos.map((p, index) => (
                                    <li key={index} className="top-list-item">
                                        <span className="item-value">
                                            {}
                                            {formatConvertedPrice(p.total)}
                                        </span>
                                        <span className="item-name">{p.nombre}</span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                        {}
                        <div className="top-list top-clientes" style={{ flex: 1 }}>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: '10px',
                                    alignItems: 'center',
                                    paddingBottom: '10px',
                                }}
                            >
                                <ClientesIcon
                                    className="formicon"
                                    style={{
                                        width: '14px',
                                        margin: '0',
                                        fill: '#2c2c2c',
                                    }}
                                />
                                <h3 className="top-list-title">Top 5 Proveedores</h3>
                            </div>
                            <ol className="top-list-items">
                                {topProveedores.map((c, index) => (
                                    <li key={index} className="top-list-item">
                                        <span className="item-value">
                                            {}
                                            {formatConvertedPrice(c.total)}
                                        </span>
                                        <span className="item-name">{c.nombre}</span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                        {}
                        <div style={{ flex: 1 }}></div>
                        <div style={{ flex: 1 }}></div>
                    </div>
                </div>
            </div>
        </div>
    );
};
export default EstadisticasCompras;
